<template>
  <div class="login-view">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-6 col-lg-5">
          <div class="card shadow-sm border-0 mt-5">
            <div class="card-body p-4">
              <h1 class="text-center mb-4">{{ isRegister ? 'Create Account' : 'Login' }}</h1>
              
              <div v-if="error" class="alert alert-danger">
                {{ error }}
              </div>
              
              <form @submit.prevent="submitForm">
                <div v-if="isRegister" class="mb-3">
                  <label for="name" class="form-label">Full Name</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    id="name" 
                    v-model="user.name"
                    required
                  >
                </div>
                
                <div class="mb-3">
                  <label for="username" class="form-label">Username</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    id="username" 
                    v-model="user.username"
                    required
                    placeholder="Enter your username"
                  >
                  <small class="form-text text-muted">Use your username to login</small>
                </div>
                
                <div v-if="isRegister" class="mb-3">
                  <label for="email" class="form-label">Email Address</label>
                  <input 
                    type="email" 
                    class="form-control" 
                    id="email" 
                    v-model="user.email"
                    required
                    placeholder="user@example.com"
                  >
                </div>
                
                <div v-if="isRegister" class="mb-3">
                  <label for="account" class="form-label">Account Number</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    id="account" 
                    v-model="user.account"
                    placeholder="Optional: Your account number"
                  >
                </div>
                
                <div class="mb-3">
                  <label for="password" class="form-label">Password</label>
                  <input 
                    type="password" 
                    class="form-control" 
                    id="password" 
                    v-model="user.password"
                    required
                  >
                </div>
                
                <div v-if="isRegister" class="mb-3">
                  <label for="confirmPassword" class="form-label">Confirm Password</label>
                  <input 
                    type="password" 
                    class="form-control" 
                    id="confirmPassword" 
                    v-model="user.confirmPassword"
                    required
                  >
                </div>
                
                <div v-if="isRegister" class="mb-3">
                  <label for="phone" class="form-label">Phone Number</label>
                  <input 
                    type="tel" 
                    class="form-control" 
                    id="phone" 
                    v-model="user.phone"
                    placeholder="e.g. 123-456-7890"
                    required
                  >
                </div>
                
                <div class="d-grid gap-2">
                  <button 
                    type="submit" 
                    class="btn btn-primary"
                    :disabled="loading"
                  >
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {{ isRegister ? 'Create Account' : 'Login' }}
                  </button>
                </div>
                
                <div class="text-center mt-3">
                  <p v-if="!isRegister && isSignupEnabled">
                    Don't have an account? 
                    <a href="#" @click.prevent="toggleRegister">Sign up</a>
                  </p>
                  <p v-else-if="isRegister">
                    Already have an account? 
                    <a href="#" @click.prevent="toggleRegister">Sign in</a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'LoginView',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    
    // State
    const isRegister = ref(false)
    const loading = ref(false)
    const error = ref(null)
    const isSignupEnabled = computed(() => store.getters['auth/isSignupEnabled'])
    
    // Check signup status when component mounts
    onMounted(async () => {
      await store.dispatch('auth/checkSignupStatus')
    })
    const user = reactive({
      name: '',
      username: '',
      email: '',
      account: '',
      password: '',
      confirmPassword: '',
      phone: ''
    })
    
    // Methods
    const toggleRegister = () => {
      isRegister.value = !isRegister.value
      error.value = null
    }
    
    const submitForm = async () => {
      error.value = null
      loading.value = true
      
      try {
        if (isRegister.value) {
          // Validation
          if (user.password !== user.confirmPassword) {
            error.value = 'Passwords do not match'
            loading.value = false
            return
          }
          
          // Register new user
          await store.dispatch('auth/register', {
            name: user.name,
            username: user.username,
            email: user.email,
            account: user.account,
            password: user.password,
            phone: user.phone
          })
          
          // After registration success, login automatically
          await store.dispatch('auth/login', {
            email: user.username, // Backend accepts username in email field for backwards compatibility
            password: user.password
          })
        } else {
          // Login existing user
          await store.dispatch('auth/login', {
            email: user.username, // Email field still used in frontend for username
            password: user.password
          })
        }
        
        // Redirect to dashboard or original requested page
        const redirectPath = route.query.redirect || '/dashboard'
        router.push(redirectPath)
      } catch (err) {
        error.value = err.response?.data?.message || 'An error occurred. Please try again.'
      } finally {
        loading.value = false
      }
    }
    
    return {
      isRegister,
      loading,
      error,
      user,
      toggleRegister,
      submitForm,
      isSignupEnabled
    }
  }
}
</script>

<style scoped>
.login-view {
  min-height: 80vh;
  display: flex;
  align-items: center;
}
</style>